import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import moment from 'moment';

import {
  ExitToApp,
  Menu as MenuIcon,
  Create,
  Notifications,
  NotificationsActive,
  CheckCircleRounded as Check,
  ThumbUpAlt,
  Settings as Config,
} from '@material-ui/icons';

import {
  AppBar,
  MenuItem,
  Menu,
  Toolbar,
  IconButton,
  Avatar,
  Typography,
  Container,
  Badge,
  ListItemText,
  MenuList,
  Divider,
} from '@material-ui/core';

import { ListItem, Tooltip } from '@mui/material';
import { green } from '@material-ui/core/colors';
import { useStyles } from './styles';
import logo from '../../assets/Ceapia-02.png';
import { Notification } from '../../models/notification';
import { useAxios } from '../../utils/hooks';
import Snackbar, { AlertComponentProps } from '../Snackbar';

const Header: React.FC = () => {
  const api = useAxios();
  const classes = useStyles();
  const history = useHistory();
  const { keycloak, initialized } = useKeycloak();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const [notificationAnchorEl, setNotificationAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const isNotificationMenuOpen = Boolean(notificationAnchorEl);

  useEffect(() => {
    async function getNotifications() {
      try {
        const notificationsResponse = await api.current?.get('/notifications');

        setNotifications(
          notificationsResponse?.data.filter(
            (notification: Notification) => !notification.read,
          ),
        );
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar notificações.',
        });
      }
    }

    if (initialized) getNotifications();
  }, [api, initialized]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleReadNotification = useCallback(
    async (notificationId: string) => {
      try {
        await api.current?.patch(`/notifications/${notificationId}`, {
          read: true,
        });

        const currentNotifications = notifications;

        const notificationIdx = currentNotifications.findIndex(
          (n) => n._id === notificationId,
        );

        currentNotifications[notificationIdx] = {
          ...currentNotifications[notificationIdx],
          read: true,
        };

        setNotifications(
          currentNotifications.filter((notification) => !notification.read),
        );
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao atualizar notificação.',
        });
      }
    },
    [api, notifications],
  );

  const renderMenu = (
    <Menu
      elevation={3}
      anchorEl={anchorEl}
      getContentAnchorEl={null}
      keepMounted
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        onClick={() => history.push('/assinatura')}
        className={classes.root}
      >
        <Create />
        Assinatura Digital
      </MenuItem>

      <MenuItem onClick={() => history.push('/conta')} className={classes.root}>
        <Config />
        Configurações
      </MenuItem>

      <MenuItem onClick={() => keycloak.logout()} className={classes.root}>
        <ExitToApp />
        Sair
      </MenuItem>
    </Menu>
  );

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      getContentAnchorEl={null}
      keepMounted
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <MenuItem
        onClick={() => history.push('/assinatura')}
        className={classes.root}
      >
        <Create />
        Assinatura Digital
      </MenuItem>

      <MenuItem onClick={() => history.push('/conta')} className={classes.root}>
        <Config />
        Configurações
      </MenuItem>

      <MenuItem onClick={() => keycloak.logout()} className={classes.root}>
        <ExitToApp />
        Sair
      </MenuItem>
    </Menu>
  );

  const renderNotificationMenu = (
    <Menu
      anchorEl={notificationAnchorEl}
      getContentAnchorEl={null}
      keepMounted
      open={isNotificationMenuOpen}
      onClose={handleNotificationMenuClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <MenuList className={classes.notificationsMenu}>
        {notifications.length > 0 &&
          notifications.map((notification: Notification) => (
            <div key={notification._id}>
              <Divider color="primary" style={{ width: '100%' }} />

              <MenuItem className={classes.notificationItem}>
                <Link
                  to={new URL(notification.link).pathname}
                  className={classes.menuItemLink}
                >
                  <ListItem
                    /* prettier-ignore */
                    secondaryAction={!notification.read && (
                    <Tooltip title="Marcar como lida">
                      <IconButton className={classes.iconButton} edge="end" aria-label="read" onClick={() => handleReadNotification(notification._id)}>
                        <Check style={{ color: green[700] }} />
                        {/* <span style={{ fontSize: '0.5rem' }}>Marcar como lida</span> */}
                      </IconButton>
                    </Tooltip>
                  )}
                  >
                    <ListItemText
                      primary={notification.message}
                      secondary={moment(notification.createdAt).format(
                        'DD/MM/YYYY hh:mm',
                      )}
                    />
                  </ListItem>
                </Link>
              </MenuItem>
            </div>
          ))}

        <Divider color="primary" style={{ width: '100%' }} />

        <div className={classes.notificationsEnd}>
          <ThumbUpAlt color="secondary" />
          <Typography className={classes.root}>Isso é tudo.</Typography>
        </div>
      </MenuList>
    </Menu>
  );

  return (
    <div className={classes.grow}>
      <AppBar position="static" className={classes.root}>
        <Container>
          <Toolbar className={classes.toolbar}>
            <img src={logo} alt="logo ceapia" className={classes.logo} />

            <div className={classes.grow} />

            {initialized && (
              <>
                <div className={classes.notifications}>
                  <Tooltip title="Notificações" placement="left">
                    <IconButton
                      onClick={handleNotificationOpen}
                      color="primary"
                    >
                      <Badge
                        badgeContent={
                          notifications.filter(
                            (notification) => !notification.read,
                          ).length
                        }
                        color="primary"
                      >
                        {notifications.length > 0 ? (
                          <NotificationsActive color="action" />
                        ) : (
                          <Notifications color="action" />
                        )}
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </div>

                <Typography className={classes.sectionDesktop}>
                  {`Olá, ${
                    // @ts-ignore
                    keycloak?.idTokenParsed?.name
                      ? // @ts-ignore
                        keycloak?.idTokenParsed?.name
                      : // @ts-ignore
                        keycloak?.idTokenParsed?.preferred_username
                  }`}
                </Typography>
              </>
            )}

            <div className={classes.sectionDesktop}>
              <IconButton
                onClick={handleProfileMenuOpen}
                color="primary"
                size="medium"
              >
                {initialized && (
                  <Avatar className={classes.avatar}>
                    {
                      // @ts-ignore
                      keycloak?.idTokenParsed?.preferred_username
                        .split('')[0]
                        .toUpperCase()
                    }
                  </Avatar>
                )}
              </IconButton>
            </div>
            <div className={classes.sectionMobile}>
              <IconButton onClick={handleMobileMenuOpen} color="primary">
                <MenuIcon className={classes.icon} />
              </IconButton>
            </div>
          </Toolbar>
        </Container>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
      {renderNotificationMenu}

      {alert.isActive && <Snackbar {...alert} />}
    </div>
  );
};

export default Header;
